import React, { useCallback, useEffect, useRef, useState } from 'react';
import { elements } from '@ecosystem/newron-design-system';
import { useWindowDimensions } from '@utils/hook/window-dimensions/WindowDimensions';
import { Feature } from '@models/Products';
import * as styles from './ProductsFeatures.module.scss';
import { random } from 'lodash';

interface Props {
  feature: Feature[] | undefined;
  firstActivity: string | undefined;
}

const ProductsFeatures: React.FC<Props> = ({ feature, firstActivity }) => {
  const ref = useRef<HTMLHeadingElement>(null);
  const refTooltip = useRef<HTMLHeadingElement>(null);
  const { viewportHeight } = useWindowDimensions();
  const calculateMargin = useCallback(() => {
    if (!refTooltip.current || !ref.current) return '0px';

    const iconRect = ref.current.getBoundingClientRect();
    const tooltip = refTooltip.current;

    const screenTopOffset = 30;
    const tooltipBottomOffset = 40;

    if (iconRect.bottom < 0) return `-${tooltip.clientHeight}px`;
    if (iconRect.top > viewportHeight) return `-0px`;

    if (iconRect.bottom + tooltip.clientHeight > viewportHeight) {
      const margin =
        iconRect.bottom < tooltip.clientHeight
          ? iconRect.bottom - screenTopOffset
          : tooltip.clientHeight - tooltipBottomOffset;
      return `-${margin}px`;
    }

    return `-29px`;
  }, [viewportHeight, ref, refTooltip]);

  const [marginTop, setMarginTop] = useState<string>(calculateMargin());

  const displayFeatureValue = (featureValue: Feature[] | undefined) => {
    const isAllFeatureValuesNull: (feat: Feature) => boolean = (feat) =>
      Object.values(feat).every((value) => value === null);

    if (featureValue === undefined || featureValue.length === 0) {
      return <li className={styles.ProductFeatures__feature}>No features available</li>;
    }

    // Case where the backend returns a list with one item only, where this (feature) item's
    // attributes are all null. This indicates that the item feature could not be loaded.
    if (featureValue.length === 1 && isAllFeatureValuesNull(featureValue[0])) {
      return <li className={styles.ProductFeatures__feature}>Can&apos;t load features for this item</li>;
    }

    return featureValue
      .filter((feat) => {
        return feat.name !== null;
      })
      .map((feat) => {
        return (
          <li key={`${feat.value}-${random(0, 1000)}`} className={styles.ProductFeatures__feature}>
            <strong className={styles.ProductFeatures__label}>{feat.name}</strong>
            <span className={styles.ProductFeatures__value}>{feat.value}</span>
          </li>
        );
      });
  };

  useEffect(() => {
    function handleScroll() {
      setMarginTop(calculateMargin());
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [calculateMargin]);

  return (
    <div id={'products-features'} className={styles.ProductsFeatures} ref={ref} data-test-id="products-features">
      <elements.Icon color="brand">
        <elements.Icon.icons.InfoOutline />
      </elements.Icon>
      <div
        className={`${styles.ProductFeatures__featureBoxContainer} z-index-tooltip`}
        data-test-id="products-features-container"
        ref={refTooltip}
        style={{ marginTop }}
      >
        <div className={styles.ProductFeatures__featureBox}>
          <h4 className={styles.ProductFeatures__headline}>Features</h4>
          <div className={styles.ProductFeatures__featureList}>
            <ul>{displayFeatureValue(feature)}</ul>
          </div>
          {firstActivity && (
            <div className={styles.ProductFeatures__firstActivity}>
              <strong>First Activity </strong>
              <span>{firstActivity}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsFeatures;
